import styles from './index.css';

import * as React from 'react';
import classNames from 'classnames';

import {Option} from '../../Definitions';
import Icon from '../Icon';
import CheckIndicator from '../CheckIndicator';
import {useHandlerOnOuterClick} from '../../Utils/Hooks';

type MultiSelectboxProps = {
	options: Option[];
	hasLoader?: boolean;
	isInvalid?: boolean;
	className?: string;
	required?: boolean;
	onSelect: (value: string|number) => void;
	selectedValues: (string|number)[];
}
export default (props: MultiSelectboxProps) => {
	const {options = [], selectedValues = [], hasLoader, isInvalid, onSelect, required, className} = props;

	if (!onSelect) {
		console.warn('Missing prop "onSelect" for ControlledMultiSelectbox.');
		return null;
	}

	if (!(hasLoader || options.length)) {
		console.warn('Missing "options" for ControlledMultiSelectbox.');
		return null;
	}

	// Toggle-state.
	const [isOpen, setIsOpen] = React.useState(false);
	const toggle = () => setIsOpen(!isOpen);
	const close = () => setIsOpen(false);
	const {node, enableHandler, disableHandler} = useHandlerOnOuterClick(close, false);

	React.useEffect(() => {
		if (isOpen) {
			enableHandler();
		} else {
			disableHandler();
		}
	}, [isOpen]);

	const isWaiting = hasLoader && options.length === 0;
	const selectedOptions = selectedValues.map((value) => options
		.find((option: Option) => option.value === value))
		.filter(Boolean);
	const isSelected = (value: number|string) => selectedValues.includes(value);

	return (
		<div
			ref={node}
			className={classNames(
				styles.multiSelectbox,
				isOpen && styles.isOpen,
				isWaiting && styles.isWaiting,
				isInvalid && styles.isInvalid,
				!selectedOptions.length && required && styles.isRequired,
				className
			)}
		>
			<div
				onClick={isWaiting ? () => {} : toggle}
				className={styles.toggler}
			>
				{
					selectedOptions.map(({value, label}: Option) => (
						<div
							key={value}
							className={styles.item}
						>
							<span>{label || value}</span>
							<Icon
								name="times"
								onClick={(e: React.SyntheticEvent) => {
									onSelect(value);
									e.stopPropagation();
								}}
								className={styles.icon}
							/>
						</div>

					))
				}
				{
					isWaiting &&
						<Icon name="spinner" rotates className={styles.icon}/>
				}
				{
					!isWaiting &&
						<Icon name="chevron-down" isFlipped={isOpen} className={styles.icon}/>
				}
			</div>
			<div className={styles.options}>
				{
					options.map(({value, label, isDisabled}: Option) => {
						const isSelectedValue = isSelected(value);
						const isSelectable = isSelectedValue || !isDisabled;

						return (
							<React.Fragment key={value}>
								<div
									key={value}
									className={classNames(styles.option, !isSelectable && styles.isDisabled)}
									onClick={() => {
										if (isSelectable) {
											onSelect(value);
										}
									}}
								>
									<CheckIndicator isChecked={isSelectedValue} className={styles.check}/>
									<span>{label || value}</span>
								</div>
							</React.Fragment>
						);
					})
				}
			</div>
		</div>
	);
}
